// Comentarios del «Análisis del bot» (repetición) y del «Entrenador de XIMA» (partida).
//
// Reglas de redacción de Yosdel (libro3-20261023):
//   - La categoría (libro, buena, error…) va UNA sola vez y dentro de una frase hablada, nunca
//     como etiqueta seca al principio («Buena. …», «Imprecisión. …»).
//   - La jugada propia no se describe con casillas; las casillas solo para la jugada mejor y
//     para lo que hace el rival.
//   - «el bloque especial», nunca «el especial» solo.
//   - Análisis: el bot habla en primera persona y en broma. Entrenador: bromista y un poco
//     sarcástico cuando te equivocas (nunca hiriente) y halagador cuando aciertas.
//   - En una misma partida no se repite una frase mientras queden otras sin usar (se elige al
//     azar entre las no usadas, con una semilla por partida: al volver atrás no cambia).
//
// Huecos de las frases:
//   {cat}/{Cat}  la categoría hablada: «una buena jugada», «un error grave», «la mejor jugada»…
//   {mejor}      mi jugada en infinitivo: «incorporar en D3» / «mover el bloque de E13 a C11»
//   {mejorP}     mi jugada en participio: «incorporado en D3»
// Las frases que usan {mejor}/{mejorP} se saltan cuando no hay jugada mejor (o ya va explicada).

// La categoría dicha como en una conversación (una sola vez por comentario).
const CATEGORY = {
  brillante: ["una jugada brillante", "una idea brillante", "una decisión brillante", "una respuesta brillante"],
  excelente: ["una jugada excelente", "una elección excelente", "una idea excelente", "una decisión excelente"],
  mejor: ["la mejor jugada", "la mejor opción", "la mejor elección", "la mejor decisión", "la mejor respuesta"],
  buena: ["una buena jugada", "una buena decisión", "una buena elección", "una buena idea", "una buena respuesta"],
  interesante: ["una jugada interesante", "una idea interesante", "una decisión interesante", "una elección interesante"],
  libro: ["una jugada de libro", "una respuesta de libro", "una elección de libro", "una idea de libro"],
  imprecision: ["una imprecisión"],
  error: ["un error"],
  grave: ["un error grave"],
};
// «jugada» se repite mucho: como mucho 1 de cada 3 comentarios y nunca en dos seguidos.
const JUGADA = /\bjugadas?\b/i;
const hasJugada = (text) => JUGADA.test(String(text || ""));
const preferNoJugada = (options, avoid) => {
  if (!avoid) return options;
  const without = options.filter((option) => !hasJugada(option));
  return without.length ? without : options;
};
const shouldAvoidJugada = (history) => {
  if (!history.length) return false;
  if (history[history.length - 1]) return true;
  const used = history.filter(Boolean).length;
  return (used + 1) * 3 > history.length + 1;
};

// Palabra de cada categoría (para las pruebas: no puede salir dos veces).
export const REPLAY_REVIEW_CATEGORY_WORDS = {
  brillante: "brillante",
  excelente: "excelente",
  mejor: "mejor",
  buena: "buena",
  interesante: "interesante",
  libro: "libro",
  imprecision: "imprecisión",
  error: "error",
  grave: "grave",
};

const BANKS = {
  // a) La persona hizo justo lo que yo habría hecho.
  same: [
    "¡Guau! Es {cat} y yo habría hecho exactamente lo mismo.",
    "Vaya, has elegido {cat}… piensas como yo y eso me preocupa.",
    "¡Eso es {cat}! ¿Me estás copiando?",
    "Has elegido {cat}, idéntica a la que yo tenía pensada. Nada que decir.",
    "{Cat}, sin discusión. Si sigues así me dejas sin trabajo.",
    "Es {cat}. Empiezo a sospechar que me lees la mente.",
    "Justo la mía, {cat}. ¿Seguro que no eres un bot?",
    "Has encontrado {cat}. Me quito el sombrero que no tengo.",
    "Es {cat}. Si te sigo dando ideas, me vas a quitar el puesto.",
    "{Cat}, igualita a la mía. Qué miedo me das.",
    "Ahí está, {cat}. Ni un pelo fuera de sitio.",
    "Es {cat}. Me estás dejando sin comentarios ingeniosos.",
  ],
  // b) Buena, excelente o brillante, pero distinta a la mía.
  good: [
    "Es {cat}… aunque no supera a la mía, yo habría {mejorP}.",
    "Has elegido {cat}. Aunque yo habría {mejorP}, por si te interesa.",
    "Es {cat}. Mi idea era {mejor}, pero la tuya también vale.",
    "Has elegido {cat}; tienes ojo, aunque yo tenía otra en mente.",
    "Me gusta, es {cat}. No es la mía, pero me gusta.",
    "Has elegido {cat}. Sigues en la pelea.",
    "{Cat}, no me lo esperaba. Te felicito.",
    "Es {cat}. Voy a fingir que yo también la había visto.",
    "Has elegido {cat}. Así da gusto revisar partidas.",
    "Es {cat}; se nota que piensas antes de mover.",
    "{Cat}. Me obliga a tomarte en serio.",
    "Has elegido {cat}. Yo tenía otra idea, pero no me voy a quejar.",
  ],
  // c) Dudosa.
  doubtful: [
    "Oye, has elegido {cat}… no me lo creo del todo.",
    "Has elegido {cat}, por no decir otra cosa.",
    "Es {cat} y arriesgada. Me gusta el valor, no tanto el resultado.",
    "Déjame pensar… es {cat}. Yo me lo pensaría dos veces.",
    "A ver, a ver… has elegido {cat}, y no me convence del todo.",
    "Es {cat}, curiosa. Yo habría {mejorP} sin pensarlo.",
    "Has elegido {cat}. Valiente, eso no te lo quito.",
    "Oye, {cat}… a tu rival le ha llamado la atención.",
    "Es {cat}, de esas que salen bien o salen muy mal.",
    "Has elegido {cat}. Me tienes intrigado, la verdad.",
    "{Cat}… me rasco la cabeza, y eso que no tengo.",
  ],
  // d) Imprecisión / error de la persona.
  inaccuracy: [
    "Has cometido {cat}. Tomo nota para usarla en tu contra.",
    "Uy, ahí se te escapó algo, es {cat}.",
    "Casi… pero no, es {cat}.",
    "Es {cat}, nada serio, pero lo apunto en mi libreta.",
    "Esto es {cat}. Me alegras el día.",
    "Has cometido {cat}; yo habría {mejorP}, solo digo.",
    "Pequeño despiste, {cat}. Nada que no se arregle.",
    "Ahí hay {cat}. Mi libreta ya está echando humo.",
    "Has cometido {cat}. Un pasito atrás, nada más.",
    "¡Uy, {cat}! Tu rival lo ha visto, te lo aseguro.",
    "Es {cat}. Sigue, pero con los ojos más abiertos.",
  ],
  error: [
    "Has cometido {cat}. Esto te puede costar.",
    "¡Ay! Eso es {cat}.",
    "Es {cat}. Lo apunto en mi libreta.",
    "Mala idea, has cometido {cat}. Esto me da ventaja, gracias.",
    "¡Ay, no! Es {cat}.",
    "Has cometido {cat}, y no lo digo por presumir… bueno, un poco sí.",
    "Aquí hay {cat} de los que se notan.",
    "Has cometido {cat}. Tu rival ya está sonriendo.",
    "Eso fue {cat}. Respira y vuelve a mirar el tablero.",
    "Es {cat}. Ahí le abriste la puerta a tu rival.",
    "¡Uy! {Cat}, y de los gordos.",
  ],
  blunder: [
    "¡Ay! Has cometido {cat}. Esto te va a costar caro.",
    "¡Ay, ay, ay! Es {cat}. Hasta yo me asusté.",
    "Eso no se hace… es {cat}.",
    "¡Uy! Esta duele, {cat}.",
    "¡Un regalo! Es {cat}, aunque no era mi cumpleaños.",
    "Has cometido {cat}. Tu rival no se lo podía creer.",
    "Es {cat}. Aquí me tapé los ojos.",
    "¡Madre mía! {Cat}.",
    "Has cometido {cat}. De esta se aprende, seguro.",
  ],
  // Persona que PERDIÓ la partida: recordar que eso pudo costar la derrota.
  errorLost: [
    "Has cometido {cat}, y eso te pudo costar la partida.",
    "Aquí empezó a torcerse todo, {cat}.",
    "Esta la vas a recordar, y no para bien, {cat}.",
    "Fue {cat} de los que se pagan. Y se pagó.",
    "Este es el momento que yo revisaría dos veces, {cat}.",
    "Aquí se te fue la partida de las manos, {cat}.",
    "Es {cat}. Si buscas el momento clave, puede ser este.",
  ],
  blunderLost: [
    "¡Vaya! Es {cat}, y eso te pudo costar la partida.",
    "Aquí se fue todo, {cat}.",
    "Si buscas dónde perdiste, es aquí, {cat}.",
    "Esta te costó caro, {cat}.",
    "Aquí empezó a torcerse todo, y bien torcido, {cat}.",
    "Es {cat}. Aquí la partida cambió de dueño.",
  ],
  // Persona que GANÓ la partida: más suave.
  errorWonBot: [
    "Te salió bien al final, pero aquí cometiste {cat} y me diste una oportunidad.",
    "Ganaste, sí, pero esto fue {cat}.",
    "Aquí me dejaste una puerta abierta con {cat}… y no la vi.",
    "Pequeño despiste, {cat}. Por suerte para ti, yo también me despisté.",
    "Al final ganaste, pero esto fue {cat}, no lo enseñes en clase.",
    "Aquí tuve mi oportunidad por {cat}. La dejé pasar… por esta vez.",
  ],
  errorWon: [
    "Te salió bien al final, pero aquí cometiste {cat}.",
    "Ganaste, sí, pero esto fue {cat}.",
    "Al final no pasó nada, pero fue {cat}.",
    "Pequeño despiste, {cat}. Por suerte, no te lo cobraron.",
    "Al final ganaste, pero esto fue {cat}, no lo enseñes en clase.",
    "Bien salvado al final, pero aquí hubo {cat}.",
  ],
  // Libro (primeras jugadas).
  book: [
    "Esta jugada es de libro. Ya creo que te la has aprendido de memoria, eso es bueno.",
    "Esta es una jugada de libro. Los primeros pasos siempre son tranquilos; ya veremos cómo se pone el juego después.",
    "Un comienzo de libro. Calentando motores.",
    "Clásico, una respuesta de libro, así empiezo yo también.",
    "Empezamos como manda el libro. Ahora empieza lo bueno.",
    "Una idea de libro, sin sorpresas. Todo en orden.",
    "Has movido como dice el libro. Se nota que has estudiado.",
    "Es una elección de libro; tranquilo, que la pelea llega después.",
    "Un paso de libro, de los de toda la vida.",
    "Jugando como manda la teoría, una jugada de libro.",
  ],
  // e) Mi propia imprecisión o error.
  botBad: [
    "Vaya, he cometido {cat}. Tomo nota, para la próxima lo haré mejor.",
    "Eso fue {cat} de mi parte… todavía me falta entrenamiento.",
    "Vale, aquí he cometido {cat}, debí {mejor}. Nadie es perfecto, ni yo.",
    "Vaya por Dios, {cat} de mi parte. No se lo cuentes a nadie.",
    "Lo reconozco, {cat}. Lo bueno era {mejor}.",
    "{Cat} de mi parte. Me pongo a entrenar ahora mismo.",
    "Aquí cometí {cat}. Hagamos como que no lo viste.",
    "Yo también me equivoco, {cat}.",
  ],
  botBadLost: [
    "Fue {cat} de mi parte… y aquí empecé a perder.",
    "Vaya, {cat} de mi parte; tú lo aprovechaste, te felicito.",
    "Aquí cometí {cat}, debí {mejor}. Lección aprendida.",
    "Me equivoqué, {cat}, y tú no perdonaste. Bien jugado.",
    "Esta me la apunto, {cat} que no se repite.",
    "Aquí cometí {cat}, y lo pagué caro.",
  ],
  // f) Mi jugada buena.
  botGood: [
    "Esta me salió preciosa, {cat}, lo admito.",
    "Es {cat}, de campeón. No es por presumir… bueno, sí.",
    "He elegido {cat}. Elegante, ¿verdad?",
    "{Cat}, precisa como un reloj. Así me gusta.",
    "Ni yo mismo me esperaba {cat}.",
    "Toma nota, que esto es {cat}.",
    "Me la enmarco, {cat}.",
    "Aquí va {cat}, para que veas cómo se hace.",
    "He jugado {cat}. Te lo dejo de ejemplo.",
    "Mira esto, {cat}. Modestia aparte.",
    "{Cat}. Hoy me siento inspirado.",
  ],
  botGoodLost: [
    "Por lo menos esta me salió bien, {cat}.",
    "Fue {cat}… aunque al final no bastó.",
    "Esta sí fue {cat}. Lástima que no fueran todas así.",
    "Aquí estuve bien, {cat}. Sigo aprendiendo.",
    "Me gustó esta, {cat}. Estoy mejorando para la próxima.",
    "Una alegría en mi derrota, {cat}.",
  ],
  botDoubtful: [
    "He elegido {cat}… no me convence ni a mí. Lo sensato era {mejor}.",
    "Lo sé, {cat}. Tenía un plan… creo.",
    "Me arriesgué un poco con {cat}. Quizá {mejor} era más sensato.",
    "Es {cat}, rara, lo sé. Pasa en las mejores familias.",
    "No es mi momento más fino, {cat}. Seguimos.",
    "Me salió {cat}. Experimentos de bot.",
  ],
  botBook: [
    "Empiezo como dice el libro, como siempre.",
    "Un paso de libro para calentar.",
    "Primeros pasos tranquilos, una respuesta de libro.",
    "Empiezo con calma, con una jugada de libro; lo bueno viene después.",
    "Jugando tranquilo, como manda el libro, sin sorpresas.",
    "Muevo según el libro, que hay que respetar la teoría.",
    "Una elección de libro, de manual. Ahora te toca.",
  ],
  // g) Si yo perdí la partida: más humilde con las jugadas buenas de la persona.
  goodBeatBot: [
    "Elegiste {cat}, no me lo esperaba.",
    "Te felicito, es {cat}.",
    "Es {cat}. Por eso me ganaste.",
    "Has elegido {cat}. Todavía me falta entrenamiento.",
    "Es {cat}. Yo habría {mejorP}, pero tú ganaste, así que tú sabrás.",
    "Es {cat}. Me la guardo para aprender.",
    "Has elegido {cat}. Me quito el sombrero.",
  ],
  sameBeatBot: [
    "Es {cat}, igual que la que yo habría hecho. Con razón me ganaste.",
    "Has elegido {cat}, la misma que la mía. Juegas muy bien.",
    "Es {cat}. Tú y yo pensamos igual… pero tú ganaste.",
    "{Cat}. Lo mismo que yo. Te felicito.",
    "Es {cat}. Estoy mejorando para alcanzarte.",
    "Has elegido {cat}; así cualquiera me gana.",
  ],
};

// Cierres del análisis cuando la mejor jugada va explicada (rotan también).
const REVIEW_BETTER = [
  "Yo habría preferido {x}.",
  "Lo mío habría sido {x}.",
  "Yo me habría decidido por {x}.",
  "Mi elección habría sido {x}.",
  "Yo en tu lugar habría optado por {x}.",
  "Lo que yo tenía en mente era {x}.",
  "Te habría convenido más {x}.",
  "Lo más fuerte era {x}.",
  "Yo habría apostado por {x}.",
  "Mi consejo habría sido {x}.",
];

// Llegada a la cima (mensaje propio, algo épico y sarcástico).
const CIMA_YOU = [
  "¡A la cima! Un lugar privilegiado al que solo unos pocos logran llegar.",
  "La cima es solo para los elegidos… y hoy te ha tocado a ti.",
  "¡Arriba del todo! Desde la cima se ve todo el tablero, ¿eh?",
  "¡Has conquistado la cima! Enfócate siempre en tu objetivo, como ahora.",
  "¡Cima conquistada! Concentración de principio a fin, así se gana.",
  "La cima te esperaba, y has llegado. Pocos lo consiguen.",
  "¡A la cima! Disfruta las vistas, te las has ganado.",
  "En la cima solo cabe uno… y eres tú.",
];
const CIMA_BOT = [
  "¡A la cima! Solo los elegidos llegan… y yo soy uno de ellos.",
  "¡Arriba del todo! La cima me sienta de maravilla.",
  "Cima conquistada. Lo siento, alguien tenía que ganar.",
  "Desde la cima se ve todo… y me encanta.",
  "¡A la cima! Enfocado en mi objetivo de principio a fin.",
];
// Consejo cuando el jugador encadena errores.
const FOCUS_TIPS = [
  "Enfócate en tu objetivo y concéntrate durante todo el juego.",
  "Un consejo, antes de mover mira qué puede hacer tu rival.",
  "Respira y céntrate en llegar a la cima, paso a paso.",
  "Concéntrate, cada movimiento cuenta en el camino a la cima.",
  "Mantén la cabeza fría; las prisas no suben pisos.",
  "Piensa en tu plan antes de tocar un bloque.",
];
// El nombre del jugador, de vez en cuando (nunca en dos comentarios seguidos), en negrita.
const NAME_GOOD = [
  "Lo estás haciendo muy bien {name} sigue así.",
  "{name} me has sorprendido con este movimiento.",
  "Muy bien {name} vas por buen camino.",
  "{name} veo que vas camino de la cima.",
  "Me estás impresionando {name} de verdad.",
  "Sigue así {name} que vas lanzado.",
];
const NAME_BAD = [
  "{name} {name} {name} eso te va a pesar más adelante.",
  "Ay {name} esa te va a costar.",
  "Cuidado {name} que tu rival no perdona.",
  "{name} piénsalo dos veces la próxima vez.",
  "Respira hondo {name} que esto se arregla.",
  "Ojo {name} que el rival está atento.",
];
const COUNTER_YOU = [
  "¡Vaya, buen contraataque por el otro lado del tablero!",
  "Veo que te escabulles por el flanco contrario, esto se pone dinámico.",
  "Contrajuego por el lado opuesto, así se le da emoción a la partida.",
  "¡Guau, la pelea se abre por los dos lados del tablero!",
  "Te vas al otro lado del tablero con un contraataque muy oportuno.",
  "Juego por las dos caras opuestas, esto se pone emocionante.",
];
const COUNTER_BOT = [
  "Y yo me voy al otro lado del tablero, a ver qué haces.",
  "Contraataco por el flanco contrario, que esto se ponga dinámico.",
  "Me escabullo por el lado opuesto, ¿me sigues?",
  "Contrajuego por el otro lado, así le doy emoción a la partida.",
];
// Juega justo la jugada que se le recomendó en su turno anterior.
const FOLLOW_GOOD = [
  "¡Por fin me hiciste caso! Así se aprende, tomando consejos de los maestros.",
  "Veo que escuchas a tu entrenador, eso me gusta.",
  "Has seguido mi consejo del turno anterior, así se aprende.",
  "Me hiciste caso y te salió de maravilla, así da gusto.",
  "Consejo escuchado y aplicado, así me gusta.",
];
const FOLLOW_BAD = [
  "Ese era el movimiento recomendado… pero en el turno anterior, no en este.",
  "Te la recomendé hace un turno, el tablero ha cambiado y ahora ya no sirve.",
  "Mi consejo caducó, eso valía en el turno anterior.",
  "Llegas un turno tarde con mi consejo, ahora ya no funciona.",
];
// Clave de una jugada para compararla con la recomendada («-d3», «=d3» o «e13c11»).
const moveKey = (described) => {
  if (!described || !cellText(described.to)) return "";
  if (isReplayReviewEntry(described)) return `${described.special ? "=" : "-"}${cellText(described.to)}`;
  return `${cellText(described.from)}${cellText(described.to)}`;
};
const NAME_EVERY = 4; // ~1 de cada 4 comentarios con nombre
// botscomentan4: Yosdel no quiere nombres en las frases (ni en el análisis ni en el Entrenador).
const NAMES_IN_COMMENTS = false;

// Texto con el nombre marcado como **Nombre**: la web lo pinta en negrita (splitNameMarkup)
// y la voz lo lee normal (lee el texto ya pintado).
export const splitNameMarkup = (text) =>
  String(text || "")
    .split(/\*\*(.+?)\*\*/)
    .map((part, index) => ({ text: part, strong: index % 2 === 1 }))
    .filter((part) => part.text);
const withName = (phrase, name) => phrase.replace(/\{name\}/g, `**${name}**`);
const cleanName = (name) => {
  const text = String(name || "").replace(/\*/g, "").trim();
  return text && text.toLowerCase() !== "player" ? text : "";
};

const BAD_KINDS = new Set(["imprecision", "error", "grave"]);

const cellText = (cell) => String(cell || "").trim().toUpperCase();

// Una entrada al tablero (notación -m1 / =m1, o origen igual al destino como «M1M1») se dice
// «incorporar en M1»; un movimiento normal, «mover el bloque de E13 a C11».
export const isReplayReviewEntry = (described) => {
  if (!described) return false;
  if (described.kind === "place") return true;
  const notation = String(described.notation || "").trim();
  if (/^[-=]/.test(notation)) return true;
  const from = cellText(described.from);
  return !from || from === cellText(described.to);
};

export const describeReplayReviewMove = (described, form = "infinitive") => {
  if (!described || !cellText(described.to)) return "";
  const to = cellText(described.to);
  if (isReplayReviewEntry(described)) {
    const what = described.special ? "el bloque especial " : "";
    return `${form === "participle" ? "incorporado" : "incorporar"} ${what}en ${to}`;
  }
  return `${form === "participle" ? "movido" : "mover"} el bloque de ${cellText(described.from)} a ${to}`;
};

const usesBest = (phrase) => phrase.includes("{mejor");

const capitalize = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : "");
const sentence = (text) => {
  const clean = String(text || "").trim();
  if (!clean) return "";
  return /[.!?]$/.test(clean) ? capitalize(clean) : `${capitalize(clean)}.`;
};

const fill = (phrase, values) =>
  phrase
    .replace(/\{Cat\}/g, capitalize(values.cat))
    .replace(/\{cat\}/g, values.cat)
    .replace(/\{mejorP\}/g, values.mejorP)
    .replace(/\{mejor\}/g, values.mejor)
    .replace(/\{x\}/g, values.x || "")
    .replace(/\s+/g, " ")
    .trim();

const hashSeed = (seed) => {
  const text = String(seed || "");
  let hash = 7;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  return hash;
};

// Elige frases sin repetir en una partida: al azar (con semilla) entre las no usadas; si ya se
// usaron todas, la usada hace más tiempo.
const makePicker = (seed) => {
  let state = hashSeed(seed) || 1;
  const random = () => {
    state = (Math.imul(state, 1103515245) + 12345) >>> 0;
    return state / 4294967296;
  };
  const lastUse = new Map();
  let tick = 0;
  return (options) => {
    if (!options.length) return "";
    const unused = options.filter((option) => !lastUse.has(option));
    const chosen = unused.length
      ? unused[Math.floor(random() * unused.length)]
      : options.reduce((a, b) => (lastUse.get(a) <= lastUse.get(b) ? a : b));
    tick += 1;
    lastUse.set(chosen, tick);
    return chosen;
  };
};

const FLOOR_WORDS = { 2: "segundo", 3: "tercer" };

// Qué grupo de frases toca a esta jugada.
const pickBank = ({ move, meta }) => {
  const players = Array.isArray(meta?.players) ? meta.players : [];
  const mover = Number(move?.playerIndex);
  const moverIsBot = Boolean(players[mover]?.isBot);
  const hasBot = players.some((player) => player?.isBot);
  const winnerIndex = Number.isFinite(Number(meta?.winnerIndex)) ? Number(meta.winnerIndex) : -1;
  const finished = winnerIndex >= 0;
  const botLost = hasBot && finished && !players[winnerIndex]?.isBot;
  const moverLost = finished && winnerIndex !== mover;
  const moverWon = finished && winnerIndex === mover;
  const kind = String(move?.kind || "");
  const same = Boolean(move?.bestIsPlayed) || kind === "mejor";

  if (moverIsBot) {
    if (kind === "libro") return { bank: "botBook" };
    if (BAD_KINDS.has(kind)) return { bank: botLost ? "botBadLost" : "botBad" };
    if (kind === "interesante") return { bank: "botDoubtful" };
    return { bank: botLost ? "botGoodLost" : "botGood" };
  }

  if (kind === "libro") return { bank: "book" };
  if (BAD_KINDS.has(kind)) {
    const base = kind === "grave" ? "blunder" : kind === "error" ? "error" : "inaccuracy";
    if (moverLost) return { bank: kind === "grave" ? "blunderLost" : "errorLost", alt: base, often: true };
    if (moverWon) return { bank: hasBot ? "errorWonBot" : "errorWon", alt: base };
    return { bank: base };
  }
  if (kind === "interesante") return { bank: "doubtful" };
  if (same) return { bank: botLost ? "sameBeatBot" : "same" };
  return { bank: botLost ? "goodBeatBot" : "good" };
};

// Texto del libro que va detrás de la frase de broma del análisis.
// Cierres del análisis cuando la jugada fue buena pero había algo más fino (como el Entrenador).
const REVIEW_BETTER_GOOD = [
  "Pero para afinar todavía más, yo habría preferido {x}.",
  "Aunque, para pulirla un poco, lo ideal habría sido {x}.",
  "Eso sí, todavía más preciso habría sido {x}.",
  "Aun así, había algo un poco más fino, que era {x}.",
  "Y ojo, que también podías {x}.",
  "Para la próxima, ten en cuenta que podías {x}.",
];
// Cuando el que se equivoca soy yo (el bot).
const BOT_BETTER = [
  "Lo sensato habría sido {x}.",
  "Tenía que haber optado por {x}.",
  "Me tocaba {x}, lo reconozco.",
  "Ahora veo que lo fuerte era {x}.",
  "Debí {x}.",
  "Lo correcto era {x}, y lo sé.",
];
// Lo que consigue una jugada mía, dicho en primera persona (el servidor lo da en segunda).
const FIRST_PERSON = [
  [/\bhas llevado tu bloque especial a la cima y has ganado\b/g, "he llevado mi bloque especial a la cima y he ganado"],
  [/amenazas ganar en tu próximo turno/g, "amenazo ganar en mi próximo turno"],
  [/\bte quedas\b/g, "me quedo"],
  [/\bhas\b/g, "he"],
  [/\btuyos\b/g, "míos"],
  [/\btuyo\b/g, "mío"],
  [/\btu\b/g, "mi"],
  [/\bamenazas\b/g, "amenazo"],
];
const toFirstPerson = (text) => FIRST_PERSON.reduce((acc, [from, to]) => acc.replace(from, to), String(text || ""));

// Todo lo que explica el Entrenador sale también aquí, como un maestro que comenta la partida
// (y en primera persona cuando el que juega soy yo).
const explainTail = (move, meta, hasBest, values, pick) => {
  const explain = move?.explain;
  const players = Array.isArray(meta?.players) ? meta.players : [];
  const moverIsBot = Boolean(players[Number(move?.playerIndex)]?.isBot);
  if (!explain || typeof explain !== "object") return { text: "" };
  const parts = [];
  const floor = FLOOR_WORDS[Number(explain.firstFloor)];
  const kind = String(move?.kind || "");
  const isBad = BAD_KINDS.has(kind) || kind === "interesante";
  const showAchieve =
    explain.achieveNotable && explain.achieve && !explain.opening && !explain.reachesTop && !(floor && /^has subido/.test(explain.achieve));
  const better = hasBest && explain.betterWhy ? `${values.mejor}${explain.betterWhy}` : "";
  let skipBest = false;
  if (moverIsBot) {
    if (explain.openingMe) parts.push(sentence(explain.openingMe));
    if (floor) parts.push(`¡Soy el primero en llegar al ${floor} piso!`);
    if (isBad) {
      if (better) {
        parts.push(fill(pick(BOT_BETTER), { ...values, x: better }));
        skipBest = true;
      }
    } else if (showAchieve) parts.push(sentence(toFirstPerson(explain.achieve)));
    return { text: parts.filter(Boolean).join(" "), skipBest };
  }
  if (explain.opening) parts.push(sentence(explain.opening));
  if (floor) parts.push(`¡Eres el primero en llegar al ${floor} piso!`);
  if (isBad) {
    if (explain.problem) parts.push(sentence(explain.problem));
    if (better) {
      parts.push(fill(pick(REVIEW_BETTER), { ...values, x: better }));
      skipBest = true;
    }
  } else {
    if (showAchieve) parts.push(sentence(explain.achieve));
    if (better && kind !== "libro") {
      parts.push(fill(pick(REVIEW_BETTER_GOOD), { ...values, x: better }));
      skipBest = true;
    }
  }
  return { text: parts.filter(Boolean).join(" "), skipBest };
};

// Devuelve { [step]: texto } para todas las jugadas analizadas. Se calcula de una vez sobre
// toda la partida y en orden, con una semilla por partida: la frase de cada jugada es siempre
// la misma (también al volver atrás) y no se repite mientras queden otras.
// options = { name, playerIndex }: nombre de quien analiza y su índice en la partida (solo
// sus jugadas llevan su nombre).
export const buildReplayReviewComments = (moves, meta, seed = "", options = {}) => {
  const list = Array.isArray(moves) ? moves : [];
  const counters = {};
  const pick = makePicker(`review|${seed}`);
  // libro6: el análisis nunca dice el nombre del jugador (tampoco en la voz ni en el video).
  const name = "";
  const namedIndex = Number.isFinite(Number(options.playerIndex)) ? Number(options.playerIndex) : -1;
  const players = Array.isArray(meta?.players) ? meta.players : [];
  const lastBad = {};
  const lastAdvice = {};
  const jugadaHistory = [];
  let nameTurn = hashSeed(seed) % NAME_EVERY;
  let lastWasNamed = false;
  const comments = {};
  const sorted = list.slice().sort((a, b) => Number(a?.step) - Number(b?.step));
  sorted.forEach((move) => {
    if (!move) return;
    const choice = pickBank({ move, meta });
    let bankName = choice.bank;
    if (choice.alt) {
      // Con derrota, 2 de cada 3 recuerdan que eso pudo costar la partida; con victoria, 1 de cada 2.
      const key = `${choice.bank}|turn`;
      const turn = counters[key] || 0;
      counters[key] = turn + 1;
      const useMain = choice.often ? turn % 3 !== 2 : turn % 2 === 0;
      if (!useMain) bankName = choice.alt;
    }
    const kind = String(move.kind || "");
    const hasBest = Boolean(move.best && cellText(move.best.to) && !move.bestIsPlayed);
    const values = {
      cat: "",
      mejor: describeReplayReviewMove(move.best, "infinitive"),
      mejorP: describeReplayReviewMove(move.best, "participle"),
    };
    // El «porqué» con el lenguaje del libro: para las jugadas de personas, y la apertura,
    // la defensa y la subida de piso también para las mías.
    const why = explainTail(move, meta, hasBest, values, pick);
    const avoidJugada = shouldAvoidJugada(jugadaHistory) || hasJugada(why.text);
    let bank = BANKS[bankName] || BANKS.good;
    bank = bank.filter((phrase) => (hasBest && !why.skipBest) || !usesBest(phrase));
    if (!bank.length) bank = ["Seguimos, {cat}."];
    const phrase = pick(preferNoJugada(bank, avoidJugada));
    values.cat = pick(preferNoJugada(CATEGORY[kind] || ["una jugada"], avoidJugada || hasJugada(phrase)));
    const mover = Number(move.playerIndex);
    const moverIsBot = Boolean(players[mover]?.isBot);
    const bad = BAD_KINDS.has(kind);
    const parts = [];
    // Nombre de vez en cuando, nunca en dos comentarios seguidos.
    let named = false;
    if (name && !moverIsBot && mover === namedIndex) {
      nameTurn += 1;
      if (NAMES_IN_COMMENTS && nameTurn % NAME_EVERY === 0 && !lastWasNamed) {
        parts.push(withName(pick(bad || kind === "interesante" ? NAME_BAD : NAME_GOOD), name));
        named = true;
      }
    }
    lastWasNamed = named;
    parts.push(fill(phrase, values));
    // ¿Jugó justo lo que le recomendé en su jugada anterior?
    const advice = lastAdvice[mover];
    if (!moverIsBot && advice && moveKey(move.played) === advice) parts.push(pick(bad || kind === "interesante" ? FOLLOW_BAD : FOLLOW_GOOD));
    lastAdvice[mover] = hasBest ? moveKey(move.best) : "";
    if (why.text) parts.push(why.text);
    const reachesTop = Boolean(move.explain?.reachesTop) || (cellText(move.played?.to) === "G7" && !move.explain && Number(move.step) === sortedLastStep(sorted));
    if (reachesTop) parts.push(pick(moverIsBot ? CIMA_BOT : CIMA_YOU));
    else if (move.explain?.counterSide && !bad && kind !== "interesante") parts.push(pick(moverIsBot ? COUNTER_BOT : COUNTER_YOU));
    if (bad && lastBad[mover] && !moverIsBot) parts.push(pick(FOCUS_TIPS));
    lastBad[mover] = bad;
    comments[Number(move.step)] = parts.join(" ");
    jugadaHistory.push(hasJugada(comments[Number(move.step)]));
  });
  return comments;
};
const sortedLastStep = (sorted) => Number(sorted[sorted.length - 1]?.step);

export const REPLAY_REVIEW_COMMENT_BANKS = BANKS;
export const REPLAY_REVIEW_BETTER_PHRASES = REVIEW_BETTER;

// ---- Entrenador de XIMA (durante la partida contra el bot) ----
// Al acertar: halagador y motivador. Al equivocarse: un poquito sarcástico, en broma, y luego
// el porqué y qué era mejor. Cada frase lleva la categoría una sola vez.
const TRAINER_BANKS = {
  mejor: [
    "¡Así se juega! Es la mejor jugada.",
    "Has elegido la mejor opción. Vas por muy buen camino.",
    "¡Lo estás haciendo muy bien! Has encontrado la mejor respuesta.",
    "Tomo nota, aprendo de ti, has tomado la mejor decisión.",
    "Has aprovechado tu turno de la mejor manera. Sigue así, vas camino de la cima.",
    "¡Qué nivel! Esa es la mejor elección.",
    "La mejor idea, sin discusión. Me gustaría haber empezado como tú en mis inicios.",
    "Has dado con la mejor jugada. ¡Así se gana!",
    "Imposible hacerlo mejor. Da gusto verte jugar.",
    "¡Bravo! Has movido de la mejor forma posible.",
    "Exacto, la mejor respuesta. Tu rival empieza a preocuparse.",
  ],
  brillante: [
    "¡Qué jugada tan brillante! Me gustaría haber empezado como tú en mis inicios.",
    "Has tenido una idea brillante. Tomo nota, aprendo de ti.",
    "Un movimiento brillante… ¡vas camino de la cima!",
    "¡Qué maravilla! Ha sido una decisión brillante.",
    "Pocos lo habrían encontrado, un recurso brillante. ¡Así se juega!",
    "Felicidades, tu idea ha sido brillante. Sigue así.",
    "Un paso brillante, de los que se recuerdan.",
    "¡Increíble! Has hecho algo brillante con tu turno.",
  ],
  excelente: [
    "¡Excelente movimiento! Vas por muy buen camino.",
    "Has tomado una decisión excelente. Lo estás haciendo muy bien.",
    "Felicidades, tu elección ha sido excelente.",
    "Una jugada excelente, tomo nota, aprendo de ti.",
    "¡Qué manera de aprovechar tu turno! Excelente. Sigue así, vas camino de la cima.",
    "Has encontrado una idea excelente, incluso más fina que la mía. ¡Así se juega!",
    "Excelente. Me gustaría haber empezado como tú en mis inicios.",
    "¡Bravo! Una respuesta excelente.",
  ],
  buena: [
    "¡Qué buena manera de aprovechar tu turno!",
    "Has tomado una buena decisión. Vas por muy buen camino.",
    "¡Muy buena jugada! Lo estás haciendo muy bien.",
    "Es una buena elección. ¡Así se juega!",
    "Felicidades, tu idea ha sido buena. Sigue así.",
    "Bien pensado, una buena respuesta.",
    "Esa posición es buena; vas camino de la cima.",
    "Una buena decisión, con calma y cabeza.",
    "Es una buena idea. Tu rival tendrá que pensar.",
    "Bien jugado, una buena forma de seguir subiendo.",
    "Has hecho una buena jugada. Tomo nota, aprendo de ti.",
  ],
  libro: [
    "Esta jugada es de libro. Ya veo que te la has aprendido de memoria, eso es estupendo.",
    "Es una respuesta de libro. Los primeros pasos siempre son tranquilos; ya veremos cómo se pone el juego después.",
    "Un comienzo de libro, vas por muy buen camino.",
    "Empiezas como dice el libro. Lo estás haciendo muy bien.",
    "Una elección de libro, como los grandes. Sigue así.",
    "Has movido como manda el libro. Se nota que has estudiado.",
    "Es una idea de libro; la teoría está de tu parte.",
    "Un paso de libro, seguro y tranquilo.",
    "Has empezado como en el libro. ¡Así se empieza!",
  ],
  interesante: [
    "Oye, tu idea es interesante… aunque no estoy seguro de que te convenga.",
    "¡Qué interesante movimiento! Arriesgado, eso sí.",
    "Has hecho una jugada interesante, pero dudosa.",
    "Tu decisión es interesante; valiente, pero había algo más seguro.",
    "Una elección interesante… tu rival la está mirando con mucha curiosidad.",
    "Interesante idea… aunque a tu rival quizá le guste más que a ti.",
    "Has tenido una ocurrencia interesante. Atrevida, como a mí me gusta… casi.",
    "Es un paso interesante, de los que salen bien o salen muy mal.",
    "Tu respuesta es interesante. Me tienes en vilo.",
    "Oye, qué manera tan interesante de usar tu turno… y tan arriesgada.",
  ],
  imprecision: [
    "Has cometido una imprecisión; tranquilo, hasta los campeones se despistan… a veces.",
    "Uy… esa imprecisión le ha encantado a tu rival.",
    "Has cometido una imprecisión. Nada serio, pero tu rival ya está sonriendo.",
    "Una pequeña imprecisión… tu rival te manda las gracias.",
    "Ojo, has cometido una imprecisión. Los despistes salen caros cerca de la cima.",
    "Has cometido una imprecisión; no pasa nada, de los despistes también se aprende.",
    "Una imprecisión… tu rival ha levantado una ceja.",
    "Has cometido una imprecisión. Casi, casi… pero no.",
    "Esa imprecisión no la he visto… bueno, sí la he visto.",
    "Una imprecisión de nada; tu rival ya la ha apuntado, eso sí.",
  ],
  error: [
    "Has cometido un error. Tu rival no se lo esperaba… y lo agradece.",
    "Uy… eso le ha encantado a tu rival, es un error.",
    "Has cometido un error; si querías hacerle un regalo a tu rival, lo has conseguido.",
    "Vaya, un error. Tranquilo, hasta los campeones se equivocan… a veces.",
    "Has cometido un error. Tu rival ya se está frotando las manos.",
    "Un error… tu rival acaba de ganar confianza, gracias a ti.",
    "Has cometido un error. ¿Era una trampa muy profunda? Me temo que no.",
    "Eso ha sido un error; tu rival te lo agradecerá en su próximo turno.",
    "Un error de los que se notan. Respira y vuelve a mirar el tablero.",
    "Has cometido un error. Generoso eres, eso sí.",
  ],
  grave: [
    "¡Uy! Has cometido un error grave. Tu rival no da crédito a su suerte.",
    "Has cometido un error grave; si buscabas ponérselo fácil a tu rival, lo has conseguido.",
    "Vaya… un error grave. Respira, que todavía se puede luchar.",
    "Ese movimiento le ha encantado a tu rival, es un error grave.",
    "Un error grave… tu rival ya está pensando en cómo celebrarlo.",
    "Has cometido un error grave. Esto no lo enseñamos en la escuela.",
    "¡Madre mía! Un error grave. Tu rival ya está sonriendo de oreja a oreja.",
    "Es un error grave; tranquilo, de los tropiezos grandes se aprende mucho.",
  ],
};

// Cierres del Entrenador (también rotan sin repetir).
const TRAINER_BETTER_BAD = [
  "Era más recomendable {x}.",
  "Te recomiendo {x}.",
  "Lo más fuerte era {x}.",
  "Lo ideal habría sido {x}.",
  "Te convenía más {x}.",
  "Para la próxima, te recomiendo {x}.",
  "Tenías una opción más sólida, que era {x}.",
  "Lo más sensato era {x}.",
  "Yo te habría sugerido {x}.",
  "Fíjate que habría sido mejor {x}.",
];
const TRAINER_BETTER_GOOD = [
  "Pero para afinar todavía más, lo mejor habría sido {x}.",
  "Aunque, para pulirla un poco más, hubiera sido mejor {x}.",
  "Pero si quieres afinar todavía más, te recomiendo {x}.",
  "Aun así, había una opción algo más precisa, que era {x}.",
  "Pero ojo, que también podías {x}.",
  "Y para la próxima, ten en cuenta que podías {x}.",
  "Aunque, para rizar el rizo, lo ideal habría sido {x}.",
  "Pero para afinar un poco más, hubiera sido mejor {x}.",
];

export const REPLAY_TRAINER_COMMENT_BANKS = TRAINER_BANKS;

const TRAINER_BAD = new Set(["interesante", "imprecision", "error", "grave"]);

// Memoria por partida del Entrenador: cada jugada (step) guarda sus frases para que el texto no
// cambie al redibujar, y las frases no se repiten en la partida mientras queden otras.
const trainerMemory = new Map();
const trainerGame = (gameKey) => {
  const key = String(gameKey || "sin-partida");
  if (!trainerMemory.has(key)) {
    if (trainerMemory.size > 20) trainerMemory.delete(trainerMemory.keys().next().value);
    trainerMemory.set(key, { pick: makePicker(`trainer|${key}`), steps: new Map(), named: new Map(), bad: new Map() });
  }
  return trainerMemory.get(key);
};
const trainerPhrase = (game, step, slot, options) => {
  const id = `${step}|${slot}`;
  if (!game.steps.has(id)) game.steps.set(id, game.pick(options));
  return game.steps.get(id);
};

// feedback = { kind, best, isBest, played, step, explain?, gameKey? } tal como llega de
// /trainer/classify (explain llega después, de /trainer/explain).
export const buildTrainerComment = (feedback) => {
  if (!feedback || !feedback.kind) return "";
  let kind = String(feedback.kind);
  if (feedback.isBest && !["brillante", "libro"].includes(kind)) kind = "mejor";
  const hasBest = Boolean(!feedback.isBest && feedback.best && cellText(feedback.best.to));
  const explain = feedback.explain && typeof feedback.explain === "object" ? feedback.explain : {};
  const mejor = hasBest ? `${describeReplayReviewMove(feedback.best, "infinitive")}${explain.betterWhy || ""}` : "";
  const floor = FLOOR_WORDS[Number(explain.firstFloor)];
  const game = trainerGame(feedback.gameKey);
  const step = Number(feedback.step) || 0;
  const bad = TRAINER_BAD.has(kind) && kind !== "interesante";
  // Nombre de vez en cuando (nunca en dos jugadas seguidas); se decide una vez por jugada.
  const name = cleanName(feedback.playerName);
  if (!game.named.has(step)) {
    const previous = Array.from(game.named.keys()).filter((s) => s < step);
    const lastNamed = previous.some((s) => s === Math.max(...previous) && game.named.get(s));
    const count = previous.length + 1;
    game.named.set(step, Boolean(name) && !lastNamed && (count + (hashSeed(feedback.gameKey) % NAME_EVERY)) % NAME_EVERY === 0);
    game.bad.set(step, bad);
  }
  const parts = [];
  if (NAMES_IN_COMMENTS && name && game.named.get(step)) parts.push(withName(trainerPhrase(game, step, "name", TRAINER_BAD.has(kind) ? NAME_BAD : NAME_GOOD), name));
  if (!game.jugada) game.jugada = new Map();
  const previousJugada = Array.from(game.jugada.keys())
    .filter((s) => s < step)
    .sort((a, b) => a - b)
    .map((s) => game.jugada.get(s));
  const avoidJugada = shouldAvoidJugada(previousJugada) || parts.some(hasJugada);
  parts.push(trainerPhrase(game, step, "intro", preferNoJugada(TRAINER_BANKS[kind] || TRAINER_BANKS.buena, avoidJugada)));
  // Recuerda la jugada que le recomendé en su turno anterior.
  if (!game.advice) game.advice = new Map();
  if (!game.advice.has(step)) game.advice.set(step, hasBest ? moveKey(feedback.best) : "");
  const earlier = Array.from(game.advice.keys()).filter((s) => s < step);
  const previousAdvice = earlier.length ? game.advice.get(Math.max(...earlier)) : "";
  if (previousAdvice && moveKey(feedback.played) === previousAdvice) {
    parts.push(trainerPhrase(game, step, "follow", TRAINER_BAD.has(kind) ? FOLLOW_BAD : FOLLOW_GOOD));
  }
  if (explain.opening) parts.push(sentence(explain.opening));
  if (floor) parts.push(`¡Eres el primero en llegar al ${floor} piso!`);
  if (TRAINER_BAD.has(kind)) {
    if (explain.problem) parts.push(sentence(explain.problem));
    if (mejor) parts.push(fill(trainerPhrase(game, step, "better", TRAINER_BETTER_BAD), { x: mejor }));
  } else {
    if (explain.reachesTop) parts.push(trainerPhrase(game, step, "cima", CIMA_YOU));
    else if (explain.counterSide) parts.push(trainerPhrase(game, step, "counter", COUNTER_YOU));
    else if (explain.achieveNotable && explain.achieve && !explain.opening && !(floor && /^has subido/.test(explain.achieve))) parts.push(sentence(explain.achieve));
    if (mejor && kind !== "libro" && kind !== "mejor") parts.push(fill(trainerPhrase(game, step, "better", TRAINER_BETTER_GOOD), { x: mejor }));
  }
  // Consejo si encadena errores (el turno propio anterior también fue malo).
  const previousSteps = Array.from(game.bad.keys()).filter((s) => s < step);
  const previousBad = previousSteps.length ? game.bad.get(Math.max(...previousSteps)) : false;
  if (bad && previousBad) parts.push(trainerPhrase(game, step, "tip", FOCUS_TIPS));
  const text = parts.filter(Boolean).join(" ");
  if (!game.jugada.has(step) || explain.problem || explain.achieve) game.jugada.set(step, hasJugada(text));
  return text;
};
